import React, { useState } from 'react';
import { Checkbox, Popover, Radio } from '@mui/material';
import { inlineFieldTypes } from '../../../utils/inlineFields';
import { inlineFieldIcon } from './inlineFieldExtension';

const optionsOf = (field) => (field?.properties?.options || []).filter((opt) => opt?.name || opt?.value);

/**
 * An inline Checkbox / Radio on the fill surface: a chip in the line of text,
 * styled like the builder's chips, showing the picked options ("Option 1,
 * Option 2") or, before any pick, the field's placeholder / label.
 *
 * Clicking it opens the options in a menu under the chip, styled like
 * `MoreActions`: a Checkbox toggles each option and stays open; a Radio picks
 * one and closes (clicking the picked one clears it). Each click is applied at
 * once. Disabled (read-only field, template preview) it shows the same text and
 * does not open.
 */
export const InlineChoiceField = ({ field, value, onChange, disabled, error }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const isCheckbox = field?.type === inlineFieldTypes.CHECK_BOX;
    const ids = isCheckbox ? value : value ? [value] : [];
    const options = optionsOf(field);
    const text = ids
        .map((optId) => options.find((opt) => opt.id === optId))
        .filter(Boolean)
        .map((opt) => opt.name || opt.value)
        .join(', ');
    const Choice = isCheckbox ? Checkbox : Radio;

    const pick = (optId) => {
        if (isCheckbox) {
            onChange(ids.includes(optId) ? ids.filter((id) => id !== optId) : [...ids, optId]);
            return;
        }
        onChange(ids.includes(optId) ? '' : optId);
        setAnchorEl(null);
    };

    return (
        <>
            <button
                type="button"
                className={`inline-field-chip inline-choice-chip${text ? ' inline-choice-chip-filled' : ''}${
                    error ? ' inline-field-control-error' : ''
                }`}
                disabled={disabled}
                aria-invalid={!!error}
                aria-haspopup="menu"
                aria-expanded={!!anchorEl}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                <img src={inlineFieldIcon(field)} alt="" className="inline-field-chip-icon" />
                {text || (
                    <span className="inline-choice-chip-empty">
                        {field?.placeholder || field?.label || (isCheckbox ? 'Checkbox' : 'Radio')}
                    </span>
                )}
            </button>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'inline-field-options-menu', sx: { minWidth: 150, mt: 0.5 } }}
            >
                <div className="more-actions-popover inline-choice-menu" role={isCheckbox ? 'group' : 'radiogroup'}>
                    {options.map((opt) => (
                        <button key={opt.id} type="button" onClick={() => pick(opt.id)}>
                            {/* MUI: the global reset (`input { appearance: none }`) leaves a native checkbox / radio invisible. */}
                            <Choice size="small" checked={ids.includes(opt.id)} tabIndex={-1} disableRipple sx={{ p: 0 }} />
                            {opt.name || opt.value}
                        </button>
                    ))}
                </div>
            </Popover>
        </>
    );
};
